import { MdOutlineArrowOutward } from 'react-icons/md';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Drawer, Flex, Popover, Text } from '@mantine/core';
import { Tabs as MantineTabs } from '@mantine/core';
import { GoDatabase } from 'react-icons/go';
import Table from '../../../../components/Table';
import { IoIosArrowDown } from 'react-icons/io';
import { LuInfo } from 'react-icons/lu';
import { idCreator } from '../../../../pages/Project/utils/idCreator';

export function Tabs({
  data,
  onChangeTable,
  projectId,
  periodId,
  selectedCategory,
  onChangeCategory,
  hideSourceList = false
}) {
  const columns2 = value => [
    {
      title: 'Tables',
      key: 'table_name'
    },
    {
      title: '',
      key: 'transformation',
      render: () => (
        <Flex
          id={`output-table-source-transform-row-${value.sheet_id}-${value.file_id}`}
          align="center"
        >
          <Text
            id={`output-table-source-transform-view-${value.sheet_id}-${value.file_id}`}
            fz="12px"
            td="underline"
            c="blue.6"
          >
            View
          </Text>
          <MdOutlineArrowOutward
            id={`output-table-source-transform-icon-${value.sheet_id}-${value.file_id}`}
            onClick={() =>
              navigate(
                `/data-extraction/${projectId}/${periodId}?sheetId=${value.sheet_id}&fileId=${value.file_id}&tab=transformation`
              )
            }
            fontSize="14px"
            style={{
              minWidth: '14px'
            }}
            color="#228BE6"
          />
        </Flex>
      )
    }
  ];

  const columns = [
    {
      title: 'File Name',
      key: 'file_name'
    },
    {
      title: 'Sheet Name',
      key: 'sheet_name'
    },
    {
      title: 'Extraction',
      key: 'extraction',
      render: item => (
        <Flex
          id={`output-table-source-extract-row-${item.sheet_id}-${item.file_id}`}
          align="center"
        >
          <Text
            id={`output-table-source-extract-view-${item.sheet_id}-${item.file_id}`}
            fz="12px"
            td="underline"
            c="blue.6"
          >
            View
          </Text>
          <MdOutlineArrowOutward
            id={`output-table-source-extract-icon-${item.sheet_id}-${item.file_id}`}
            onClick={() =>
              navigate(
                `/data-extraction/${projectId}/${periodId}?sheetId=${item.sheet_id}&fileId=${item.file_id}`
              )
            }
            fontSize="14px"
            style={{
              minWidth: '14px'
            }}
            color="#228BE6"
          />
        </Flex>
      )
    },
    {
      title: 'Transformation',
      key: 'transformation',
      render: item => {
        return item.transformations.length > 0 ? (
          <Popover width={341} position="bottom" shadow="md">
            <Popover.Target>
              <Flex
                id={`output-table-source-transform-popover-${item.sheet_id}-${item.file_id}`}
                sx={{ cursor: 'pointer' }}
                align="center"
              >
                <Text
                  id={`output-table-source-transform-link-${item.sheet_id}-${item.file_id}`}
                  fz="12px"
                  td="underline"
                  c="blue.6"
                >
                  View Tables
                </Text>
                <IoIosArrowDown
                  id={`output-table-source-transform-dropdown-${item.sheet_id}-${item.file_id}`}
                  onClick={() =>
                    navigate(
                      `/data-extraction/${projectId}/${periodId}?sheetId=${item.sheet_id}&fileId=${item.file_id}`
                    )
                  }
                  fontSize="14px"
                  style={{
                    minWidth: '14px'
                  }}
                  color="#228BE6"
                />
              </Flex>
            </Popover.Target>
            <Popover.Dropdown>
              <Box
                sx={() => ({
                  overflow: 'auto',
                  maxHeight: '300px'
                })}
              >
                <Table
                  idPrefix={`output-table-transformations-${item.sheet_id}-${item.file_id}`}
                  hasIndex
                  columns={columns2(item)}
                  data={item.transformations}
                />
              </Box>
            </Popover.Dropdown>
          </Popover>
        ) : (
          ''
        );
      }
    }
  ];

  const [selectedTable, setSelectedTable] = useState(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const { regularCategories, calculatedCategories } = useMemo(() => {
    if (!data) return { regularCategories: [], calculatedCategories: [] };
    return {
      regularCategories: data.filter(c => c.type !== 'calculated'),
      calculatedCategories: data.filter(c => c.type === 'calculated')
    };
  }, [data]);

  const isDirectAnswersActive = selectedCategory?.type === 'calculated';
  const topTabValue = isDirectAnswersActive ? '__direct_answers__' : selectedCategory?.name;

  const handleTableSelect = table => {
    setSelectedTable(table);
    onChangeTable(table);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (selectedCategory) return;

    // Guard against an empty payload (e.g. Conversion workflows expose no
    // output-table categories) — `data` is a truthy `[]`, so `data[0]` would
    // be undefined and reading `.tables` throws.
    if (data?.length) {
      onChangeCategory(data[0]);
      setSelectedTable(data[0].tables[0]);
      onChangeTable(data[0].tables[0]);
    }
  }, [data, selectedCategory]);

  const onSelectCategory = value => {
    onChangeCategory(value);
    setSelectedTable(value.tables[0]);
    onChangeTable(value.tables[0]);
  };

  const onSelectDirectAnswers = () => {
    if (calculatedCategories.length === 0) return;
    onSelectCategory(calculatedCategories[0]);
  };

  const onCheckWarning = category => {
    for (let i = 0; i < category.tables.length; i++) {
      if (category.tables[i].warnings.length > 0) {
        return true;
      }
    }
    return false;
  };

  const onCheckError = category => {
    for (let i = 0; i < category.tables.length; i++) {
      if (category.tables[i].errors.length > 0) {
        return true;
      }
    }
    return false;
  };

  const onCheckMandatory = category => {
    for (let i = 0; i < category.tables.length; i++) {
      if (category.tables[i].mandatory_fixes.length > 0) {
        return true;
      }
    }
    return false;
  };

  const getCategoryErrorIcon = category => {
    if (onCheckError(category)) return <LuInfo color="#E03131" fontSize="16px" />;
    if (onCheckMandatory(category)) return <LuInfo color="#FD7E14" fontSize="16px" />;
    if (onCheckWarning(category)) return <LuInfo color="#868E96" fontSize="16px" />;

    return <></>;
  };

  const getDirectAnswersErrorIcon = () => {
    for (const cat of calculatedCategories) {
      if (onCheckError(cat)) return <LuInfo color="#E03131" fontSize="16px" />;
    }
    for (const cat of calculatedCategories) {
      if (onCheckMandatory(cat)) return <LuInfo color="#FD7E14" fontSize="16px" />;
    }
    for (const cat of calculatedCategories) {
      if (onCheckWarning(cat)) return <LuInfo color="#868E96" fontSize="16px" />;
    }
    return <></>;
  };

  const getTableErrorIcon = table => {
    if (table.errors.length > 0) return <LuInfo color="#E03131" fontSize="16px" />;
    if (table.mandatory_fixes.length > 0) return <LuInfo color="#FD7E14" fontSize="16px" />;
    if (table.warnings.length > 0) return <LuInfo color="#868E96" fontSize="16px" />;

    return <></>;
  };

  return (
    <Box
      id="output-table-tabs"
      bg="#fff"
      p="8px"
      sx={() => ({
        borderRadius: '4px',
        border: '1px solid #F1F3F5'
      })}
      mt="12px"
    >
      <Flex id="output-table-tabs-header" justify="space-between" align="center" gap="100px">
        <MantineTabs value={topTabValue}>
          <MantineTabs.List id="output-table-top-tabs-list">
            {regularCategories.map(category => (
              <MantineTabs.Tab
                id={`output-table-category-tab-${idCreator(category.name)}`}
                onClick={() => onSelectCategory(category)}
                key={category.name}
                value={category.name}
                leftSection={getCategoryErrorIcon(category)}
                lh="16px"
              >
                <Flex align="center" gap="4px">
                  {category.name}
                  {category.question && (
                    <Text fz="10px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      (Q{category.question})
                    </Text>
                  )}
                </Flex>
              </MantineTabs.Tab>
            ))}
            {calculatedCategories.length > 0 && (
              <MantineTabs.Tab
                id="output-table-direct-answers-tab"
                onClick={onSelectDirectAnswers}
                value="__direct_answers__"
                leftSection={getDirectAnswersErrorIcon()}
                lh="16px"
              >
                Direct answers
              </MantineTabs.Tab>
            )}
          </MantineTabs.List>
        </MantineTabs>
        {!hideSourceList && (
          <Button
            id="output-table-source-list-button"
            onClick={() => setIsOpen(true)}
            leftSection={<GoDatabase />}
            variant="subtle"
            size="xs"
          >
            Source List
          </Button>
        )}
      </Flex>

      {isDirectAnswersActive && (
        <MantineTabs mt="4px" value={selectedCategory?.name}>
          <MantineTabs.List id="output-table-direct-tabs-list">
            {calculatedCategories.map(category => (
              <MantineTabs.Tab
                id={`output-table-direct-category-tab-${idCreator(category.name)}`}
                onClick={() => onSelectCategory(category)}
                key={category.name}
                value={category.name}
                leftSection={getCategoryErrorIcon(category)}
                lh="16px"
                fz="12px"
              >
                <Flex align="center" gap="4px">
                  {category.name}
                  {category.question && (
                    <Text fz="10px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      (Q{category.question})
                    </Text>
                  )}
                </Flex>
              </MantineTabs.Tab>
            ))}
          </MantineTabs.List>
        </MantineTabs>
      )}

      <MantineTabs mt="10px" value={selectedTable?.name} w="fit-content">
        <MantineTabs.List id="output-table-table-tabs-list">
          {selectedCategory?.tables?.map(table => (
            <MantineTabs.Tab
              id={`output-table-table-tab-${idCreator(table.name)}`}
              onClick={() => handleTableSelect(table)}
              key={table.name}
              value={table.name}
              leftSection={getTableErrorIcon(table)}
              lh="16px"
            >
              {table.name}
            </MantineTabs.Tab>
          ))}
        </MantineTabs.List>
      </MantineTabs>
      <Drawer
        id="output-table-source-drawer"
        closeButtonProps={{ id: 'output-table-source-drawer-close' }}
        opened={isOpen}
        onClose={onClose}
        position="right"
        title="Source List"
        size="lg"
        zIndex={1100}
      >
        {data?.map(
          category =>
            category?.sources?.length > 0 && (
              <Box
                id={`output-table-source-group-${idCreator(category.name)}`}
                key={`source-${category.name}`}
                mb="24px"
              >
                <Text
                  id={`output-table-source-group-label-${idCreator(category.name)}`}
                  mb="8px"
                  fz="14px"
                  c="#000"
                  fw={600}
                >
                  {category.name}
                </Text>
                <Table
                  idPrefix={`output-table-source-table-${idCreator(category.name)}`}
                  columns={columns}
                  data={category?.sources || []}
                />
              </Box>
            )
        )}
      </Drawer>
    </Box>
  );
}
